// 외부 JSON API 클라이언트 (대전민들레로타리클럽)

import { clearMemberToken, getMemberToken } from "@/lib/auth";

export const API_BASE_URL = "https://djmin.eemo.co.kr/api/";
export const API_TOKEN = "MDL-2026-CHANGE-ME";

// 401(세션 만료) 발생 시 앱 전역에 알리는 이벤트 이름
export const UNAUTHORIZED_EVENT = "mdl:unauthorized";

// 상태 처리 공통 문구
export const MSG_LOAD_FAILED = "일시적으로 불러오지 못했습니다.";

export interface ApiResponse<T> {
  rst: number;
  msg: string;
  data: T;
  total: number;
  js_key?: string;
  center_lat?: number | string;
  center_lon?: number | string;
  server_ts?: number;
  server_time?: string;
  [key: string]: unknown;
}

interface RequestInitLite {
  method?: string;
  headers?: Record<string, string>;
  body?: string | FormData;
}

async function request<T>(
  path: string,
  init?: RequestInitLite
): Promise<ApiResponse<T>> {
  const token = getMemberToken();

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        "X-API-TOKEN": API_TOKEN,
        ...(token ? { "X-MEMBER-TOKEN": token } : {}),
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    // 네트워크 오류
    throw new Error(MSG_LOAD_FAILED);
  }

  // 세션 만료 → 토큰 정리 후 로그인 모달 요청
  if (res.status === 401) {
    clearMemberToken();
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    const err = new Error("로그인이 필요합니다.");
    (err as Error & { status?: number }).status = 401;
    throw err;
  }

  const text = await res.text();
  let json: ApiResponse<T> | null = null;
  try {
    json = JSON.parse(text) as ApiResponse<T>;
  } catch {
    json = null;
  }

  // JSON 이 아닌 응답(404 포함) 또는 잘못된 응답
  if (!json) {
    throw new Error(MSG_LOAD_FAILED);
  }

  if (json.rst !== 1) {
    const err = new Error(json.msg || "요청에 실패했습니다.");
    (err as Error & { code?: number; status?: number }).code = json.rst;
    (err as Error & { code?: number; status?: number }).status = res.status;
    throw err;
  }

  return json;
}

export function apiGet<T>(path: string): Promise<ApiResponse<T>> {
  return request<T>(path);
}

export function apiPost<T>(
  path: string,
  body: Record<string, string | number | undefined>
): Promise<ApiResponse<T>> {
  const params = new URLSearchParams();
  Object.entries(body).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });

  return request<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params.toString(),
  });
}

// multipart/form-data 업로드 (직업홍보 · 자유글 글쓰기 등)
export function apiPostForm<T>(path: string, form: FormData): Promise<ApiResponse<T>> {
  return request<T>(path, {
    method: "POST",
    body: form,
  });
}